import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ArrowRight, Check, Loader2, FileText } from 'lucide-react';
import InvoiceRequestDialog from '@/components/InvoiceRequestDialog';

type BillingCycle = 'monthly' | 'annual';

const PRICE_IDS: Record<BillingCycle, string> = {
  monthly: 'price_1TRq1NKHBfrv5p55TJYmuMXW',
  annual: 'price_1TRq1NKHBfrv5p555RhA4yYr',
};

const FREE_FEATURES = [
  '10 assessments/month',
  '1 class',
  '10 pupils',
  'AI writing assessment',
  'Multi-page assessment',
  'Progress tracking',
];

const PAID_FEATURES = [
  '50 assessments/month',
  '2 classes',
  '40 pupils',
  'AI writing assessment',
  'Multi-page assessment',
  'Automated report comments',
  'Progress tracking & reports',
  'CSV pupil import',
];

interface SchoolTier {
  id: string;
  name: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  monthlyPriceId: string;
  annualPriceId: string;
  features: string[];
}

const SCHOOL_TIERS: SchoolTier[] = [
  {
    id: 'starter',
    name: 'School Starter',
    description: 'Small schools (1-form entry).',
    monthlyPrice: '£49/mo',
    annualPrice: '£490/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p558aMxk9s2',
    annualPriceId: 'price_1TRq1PKHBfrv5p550u2sotCa',

    features: ['200 assessments/month', 'Up to 10 teachers', 'Unlimited classes & pupils', 'Invite & manage teachers', 'Admin assessment visibility'],
  },
  {
    id: 'standard',
    name: 'School Standard',
    description: 'Average 2-form entry schools.',
    monthlyPrice: '£89/mo',
    annualPrice: '£890/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p55dxh2IL4f',
    annualPriceId: 'price_1TRq1PKHBfrv5p556fMyL98e',

    features: ['500 assessments/month', 'Up to 30 teachers', 'Unlimited classes & pupils', 'Invite & manage teachers', 'Admin assessment visibility'],
  },
  {
    id: 'plus',
    name: 'School Plus',
    description: 'Large schools (3+ form entry).',
    monthlyPrice: '£149/mo',
    annualPrice: '£1,490/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p55cDKpiRle',
    annualPriceId: 'price_1TRq1PKHBfrv5p55nXrBHQCp',

    features: ['1,000 assessments/month', 'Unlimited teachers', 'Unlimited classes & pupils', 'Invite & manage teachers', 'Admin assessment visibility'],
  },
];

export default function IndividualBilling() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [loading, setLoading] = useState<'free' | 'paid' | null>(null);
  const [invoiceDialog, setInvoiceDialog] = useState<{ tier: SchoolTier; priceId: string } | null>(null);

  // Free tier has nothing to pay, so it's activated directly rather than via a £0 Stripe Checkout.
  const handleContinueFree = async () => {
    setLoading('free');
    const { data, error } = await supabase.functions.invoke('activate-free-plan');
    if (error || !data?.ok) {
      toast({
        title: 'Something went wrong',
        description: (data as { error?: string } | null)?.error ?? error?.message ?? 'Unable to activate the Free plan. Please try again.',
        variant: 'destructive',
      });
      setLoading(null);
      return;
    }
    navigate('/dashboard?upgrade=success');
  };

  const handleCheckout = async (priceId: string, type: 'free' | 'paid') => {
    setLoading(type);
    const { data, error } = await supabase.functions.invoke('stripe-checkout', {
      body: { priceId },
    });
    if (error || !data?.url) {
      toast({
        title: 'Something went wrong',
        description: error?.message ?? 'Unable to start checkout. Please try again.',
        variant: 'destructive',
      });
      setLoading(null);
      return;
    }
    window.location.href = data.url;
  };

  // Individual users have no school yet — school plan selection requires school setup first.
  // We store the intended priceId in sessionStorage so school-billing can pre-select it after setup.
  const handleSchoolCardPayment = (tier: SchoolTier) => {
    const priceId = billingCycle === 'monthly' ? tier.monthlyPriceId : tier.annualPriceId;
    sessionStorage.setItem('pendingSchoolPriceId', priceId);
    navigate('/school-setup');
  };

  const handleRequestInvoice = (tier: SchoolTier) => {
    const priceId = billingCycle === 'monthly' ? tier.monthlyPriceId : tier.annualPriceId;
    sessionStorage.setItem('pendingSchoolPriceId', priceId);
    navigate('/school-setup');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-4xl space-y-10">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Choose your plan</h1>
          <p className="text-muted-foreground">
            Your classes are ready. Now choose how you'd like to use WriteAssess.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex justify-center">
          <div className="inline-flex items-center rounded-full border bg-muted/40 p-1 gap-1">
            <button
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <span className="ml-2 text-xs text-primary font-semibold">Save £18</span>
            </button>
          </div>
        </div>

        {/* Individual plan cards */}
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Free plan */}
          <Card className="shadow-soft border-2 border-border flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl">Free plan</CardTitle>
              <CardDescription>Continue with no cost, forever.</CardDescription>
              <div className="mt-2">
                <span className="text-3xl font-bold">£0</span>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 gap-4">
              <ul className="space-y-2 flex-1">
                {FREE_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full"
                variant="outline"
                disabled={!!loading}
                onClick={handleContinueFree}
              >
                {loading === 'free'
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <ArrowRight className="ml-2 h-4 w-4" />}
                Continue free
              </Button>
            </CardContent>
          </Card>

          {/* Paid individual plan */}
          <Card className="shadow-soft border-2 border-primary flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                Recommended
              </span>
            </div>
            <CardHeader className="pb-3 pt-6">
              <CardTitle className="text-xl">Individual</CardTitle>
              <CardDescription>Full power for independent teachers.</CardDescription>
              <div className="mt-2">
                {billingCycle === 'monthly' ? (
                  <span className="text-3xl font-bold">£9<span className="text-base font-normal text-muted-foreground">/mo</span></span>
                ) : (
                  <>
                    <span className="text-3xl font-bold">£90<span className="text-base font-normal text-muted-foreground">/yr</span></span>
                    <span className="ml-2 text-sm text-primary font-medium">Save £18</span>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 gap-4">
              <ul className="space-y-2 flex-1">
                {PAID_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full"
                disabled={!!loading}
                onClick={() => handleCheckout(PRICE_IDS[billingCycle], 'paid')}
              >
                {loading === 'paid'
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <ArrowRight className="mr-2 h-4 w-4" />}
                Upgrade
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* School plan section */}
        <div className="space-y-4">
          <div className="text-center space-y-1">
            <h2 className="text-xl font-semibold">Buying for your school?</h2>
            <p className="text-sm text-muted-foreground">
              School plans include unlimited pupils and classes, teacher management, and invoicing for finance teams.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {SCHOOL_TIERS.map((tier) => (
              <Card
                key={tier.id}
                className="shadow-soft border-2 border-border hover:border-primary/50 transition-colors flex flex-col"
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{tier.name}</CardTitle>
                  <CardDescription>{tier.description}</CardDescription>
                  <div className="mt-1">
                    <span className="text-xl font-bold">
                      {billingCycle === 'monthly' ? tier.monthlyPrice : tier.annualPrice}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 gap-3">
                  <ul className="space-y-1.5 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col gap-2">
                    <Button
                      className="w-full"
                      size="sm"
                      onClick={() => handleSchoolCardPayment(tier)}
                    >
                      <ArrowRight className="mr-2 h-3.5 w-3.5" />
                      Pay by Card
                    </Button>
                    <Button
                      className="w-full"
                      size="sm"
                      variant="outline"
                      onClick={() => handleRequestInvoice(tier)}
                    >
                      <FileText className="mr-2 h-3.5 w-3.5" />
                      Request Invoice
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* VAT notice */}
        <p className="text-center text-xs text-muted-foreground pt-2">
          WriteAssess is not currently VAT registered, so VAT is not charged. Prices shown are the total amount you pay.
        </p>
      </div>

      {invoiceDialog && (
        <InvoiceRequestDialog
          open={true}
          onOpenChange={(open) => { if (!open) setInvoiceDialog(null); }}
          priceId={invoiceDialog.priceId}
          planName={invoiceDialog.tier.name}
        />
      )}
    </div>
  );
}
